import * as THREE from 'three'
import Terrain from '../terrain'
import { InventoryItemType } from '../items'

// Shared hurt-flash material (one instance for all mobs, never disposed)
const hurtFlashMaterial = new THREE.MeshStandardMaterial({
  color: 0xff4444,
  emissive: 0xff0000,
  emissiveIntensity: 0.5
})

export abstract class Mob {
  scene: THREE.Scene
  terrain: Terrain

  mesh: THREE.Group
  speed: number = 2.0
  gravity: number = -18
  velocity: THREE.Vector3 = new THREE.Vector3()
  grounded: boolean = false
  isAlive: boolean = true
  dropsSpawned: boolean = false
  followTarget: THREE.Object3D | null = null
  isBoarded = false
  isHostile = false
  countsAsArkAnimal = true
  onDeath?: () => void

  // Health & damage
  maxHealth: number = 10
  health: number = 10
  hurtTimer: number = 0
  hurtCooldown: number = 0.5 // seconds of invulnerability after hit
  isDying: boolean = false
  deathTimer: number = 0
  private savedMaterials: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map()
  private flashActive = false

  // For wandering
  targetDir: THREE.Vector3 = new THREE.Vector3()
  state: 'idle' | 'walking' = 'idle'
  stateTimer: number = 0

  // vanilla-style behaviors
  burnsInSunlight = false // only zombies/skeletons burn at dawn
  panicTimer = 0 // passives sprint in random directions after being hurt
  panicDirTimer = 0
  lastDamageDir = new THREE.Vector3()
  hasDamageDir = false
  temptTarget: THREE.Object3D | null = null // player holding food nearby
  lookTarget: THREE.Object3D | null = null // player camera, for head tracking
  headPart: THREE.Object3D | null = null // registered by subclasses
  headLookActive = false
  lavaDamageTimer = 0

  // collision body
  bodyRadius = 0.35
  jumpVelocity = 7.5
  blockedX = false
  blockedZ = false

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    this.scene = scene
    this.terrain = terrain
    this.mesh = new THREE.Group()
    this.mesh.position.copy(position)
    
    this.scene.add(this.mesh)
  }

  abstract updateAnimation(delta: number): void

  getDrops(): InventoryItemType[] {
    return []
  }

  update(delta: number) {
    if (!this.isAlive) return

    // Death animation
    if (this.isDying) {
      this.deathTimer += delta
      this.mesh.rotation.z += delta * 6
      const s = Math.max(0, 1 - this.deathTimer * 2)
      this.mesh.scale.set(s, s, s)
      this.mesh.position.y += delta * 2 // float up slightly
      if (this.deathTimer > 0.6) {
        this.despawn()
      }
      return
    }

    // Hurt flash timer
    if (this.hurtTimer > 0) {
      this.hurtTimer -= delta
      // Flash red/white — shared material, only traverse when the state flips
      const flash = this.hurtTimer > 0 && Math.sin(this.hurtTimer * 30) > 0
      if (flash !== this.flashActive) {
        this.flashActive = flash
        this.mesh.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (flash) {
              if (!this.savedMaterials.has(child)) {
                this.savedMaterials.set(child, child.material)
              }
              child.material = hurtFlashMaterial
            } else {
              const saved = this.savedMaterials.get(child)
              if (saved) child.material = saved
            }
          }
        })
      }
      if (this.hurtTimer <= 0) {
        this.savedMaterials.clear()
      }
    }

    // Physics
    const waterSurface = this.terrain.getWaterSurfaceYAt(
      this.mesh.position.x,
      this.mesh.position.z
    )
    const inWater =
      waterSurface !== null && this.mesh.position.y <= waterSurface + 0.7
    const inLava =
      !inWater &&
      this.terrain.isLavaAt(
        Math.round(this.mesh.position.x),
        Math.round(this.mesh.position.y + 0.2),
        Math.round(this.mesh.position.z)
      )

    if (inWater && waterSurface !== null) {
      this.velocity.y = Math.min(
        2.6,
        this.velocity.y +
          (this.mesh.position.y < waterSurface + 0.45 ? 10 : -2) * delta
      )
      this.velocity.x *= 0.92
      this.velocity.z *= 0.92
    } else if (inLava) {
      // thick buoyancy + burning
      this.velocity.y = Math.min(1.4, this.velocity.y + 6 * delta)
      this.velocity.x *= 0.85
      this.velocity.z *= 0.85
      this.lavaDamageTimer += delta
      if (this.lavaDamageTimer >= 0.5) {
        this.lavaDamageTimer = 0
        this.takeDamage(2)
      }
    } else {
      this.lavaDamageTimer = 0
      this.velocity.y += this.gravity * delta
    }
    this.mesh.position.y += this.velocity.y * delta

    // AI / Wandering
    this.updateAI(delta, inWater || inLava)

    // Horizontal movement with terrain collision (per axis, so mobs slide
    // along walls) + vanilla-style step-up jump when a 1-block ledge blocks
    // the way
    this.moveHorizontally(delta)
    if ((this.blockedX || this.blockedZ) && this.state === 'walking') {
      this.handleBlocked()
    }

    // Ground collision: stand on the highest solid cell directly below
    // (sees placed blocks, mined-out pits, and cave openings alike).
    // Skip the snap while moving upward or a fresh step-up jump would be
    // cancelled in the same frame it was issued.
    const groundY = this.findSupportY()
    if (
      this.velocity.y <= 0 &&
      groundY !== null &&
      this.mesh.position.y <= groundY
    ) {
      this.mesh.position.y = groundY
      this.velocity.y = 0
      this.grounded = true
    } else {
      this.grounded = false
    }

    this.updateAnimation(delta)
    this.updateHeadLook(delta)
  }

  // axis-separated horizontal integration; sets blockedX/blockedZ
  moveHorizontally(delta: number) {
    const position = this.mesh.position
    this.blockedX = false
    this.blockedZ = false

    if (this.velocity.x !== 0) {
      const nextX = position.x + this.velocity.x * delta
      const probeX = nextX + Math.sign(this.velocity.x) * this.bodyRadius
      if (this.isBlockedAt(probeX, position.z)) {
        this.blockedX = true
      } else {
        position.x = nextX
      }
    }

    if (this.velocity.z !== 0) {
      const nextZ = position.z + this.velocity.z * delta
      const probeZ = nextZ + Math.sign(this.velocity.z) * this.bodyRadius
      if (this.isBlockedAt(position.x, probeZ)) {
        this.blockedZ = true
      } else {
        position.z = nextZ
      }
    }
  }

  // solid at foot or torso level blocks walking
  isBlockedAt(x: number, z: number) {
    const feetY = this.mesh.position.y
    return (
      this.terrain.isSolidForMobAt(x, feetY + 0.1, z) ||
      this.terrain.isSolidForMobAt(x, feetY + 1.0, z)
    )
  }

  // default response to a wall: hop up 1-block steps (spiders climb instead)
  handleBlocked() {
    if (!this.grounded) return
    const position = this.mesh.position
    const aheadX = position.x + Math.sign(this.velocity.x || this.targetDir.x) * (this.bodyRadius + 0.5)
    const aheadZ = position.z + Math.sign(this.velocity.z || this.targetDir.z) * (this.bodyRadius + 0.5)
    const headroomClear =
      !this.terrain.isSolidForMobAt(aheadX, position.y + 1.2, aheadZ) &&
      !this.terrain.isSolidForMobAt(aheadX, position.y + 2.0, aheadZ) &&
      !this.terrain.isSolidForMobAt(position.x, position.y + 2.0, position.z)
    if (headroomClear) {
      this.velocity.y = this.jumpVelocity
      this.grounded = false
    }
  }

  // top surface of the nearest solid cell at or below the feet (24 cells)
  findSupportY(): number | null {
    const x = this.mesh.position.x
    const z = this.mesh.position.z
    const startY = Math.round(this.mesh.position.y)
    const minY = Math.max(0, startY - 24)
    for (let cy = startY; cy >= minY; cy--) {
      if (this.terrain.isSolidForMobAt(x, cy, z)) {
        return cy + 0.5
      }
    }
    return null
  }

  // passives turn their heads toward a nearby player when not sprinting away
  updateHeadLook(delta: number) {
    if (!this.headPart || this.isHostile || this.isDying) return

    let wantLook = false
    let localAngle = 0
    const target = this.lookTarget
    if (target && this.panicTimer <= 0) {
      const dx = target.position.x - this.mesh.position.x
      const dz = target.position.z - this.mesh.position.z
      if (dx * dx + dz * dz < 36) {
        localAngle = Math.atan2(dx, dz) - this.mesh.rotation.y
        while (localAngle < -Math.PI) localAngle += Math.PI * 2
        while (localAngle > Math.PI) localAngle -= Math.PI * 2
        if (Math.abs(localAngle) < 1.6) {
          wantLook = true
          localAngle = THREE.MathUtils.clamp(localAngle, -0.7, 0.7)
        }
      }
    }

    if (wantLook) {
      this.headLookActive = true
      this.headPart.rotation.y = THREE.MathUtils.lerp(
        this.headPart.rotation.y,
        localAngle,
        Math.min(1, delta * 6)
      )
    } else if (this.headLookActive) {
      this.headPart.rotation.y = THREE.MathUtils.lerp(
        this.headPart.rotation.y,
        0,
        Math.min(1, delta * 6)
      )
      if (Math.abs(this.headPart.rotation.y) < 0.02) {
        this.headPart.rotation.y = 0
        this.headLookActive = false
      }
    }
  }

  /**
   * Deal damage to this mob. Direction is from attacker to mob (for knockback).
   */
  takeDamage(amount: number, knockbackDir?: THREE.Vector3) {
    if (this.hurtTimer > 0 || this.isDying || !this.isAlive) return

    this.health -= amount
    this.hurtTimer = this.hurtCooldown

    // passives panic-sprint away from whatever hurt them
    if (!this.isHostile) {
      this.panicTimer = 5
      this.panicDirTimer = 0
      if (knockbackDir) {
        this.lastDamageDir.copy(knockbackDir)
        this.lastDamageDir.y = 0
        if (this.lastDamageDir.lengthSq() > 0.001) {
          this.lastDamageDir.normalize()
          this.hasDamageDir = true
        }
      }
    }

    // Knockback
    if (knockbackDir) {
      const kb = knockbackDir.clone().normalize()
      this.velocity.x = kb.x * 8
      this.velocity.z = kb.z * 8
      this.velocity.y = 5
      this.grounded = false
    }

    if (this.health <= 0) {
      this.health = 0
      this.isDying = true
      this.deathTimer = 0
      this.onDeath?.()
    }
  }

  setFollowTarget(target: THREE.Object3D | null) {
    this.followTarget = target
    if (target) {
      this.isBoarded = false
    }
    this.stateTimer = 0
  }

  toggleFollowTarget(target: THREE.Object3D) {
    if (this.followTarget === target) {
      this.setFollowTarget(null)
      return false
    }
    this.setFollowTarget(target)
    return true
  }

  isFollowing() {
    return this.followTarget !== null
  }

  updateFollowTarget(delta: number) {
    if (!this.followTarget) return false

    const toTarget = new THREE.Vector3().subVectors(
      this.followTarget.position,
      this.mesh.position
    )
    toTarget.y = 0
    const distance = toTarget.length()

    if (distance > 36) {
      this.setFollowTarget(null)
      return true
    }

    if (distance > 2.2) {
      const direction = toTarget.normalize()
      this.velocity.x = direction.x * this.speed * 1.75
      this.velocity.z = direction.z * this.speed * 1.75
      this.state = 'walking'

      const targetRotation = Math.atan2(this.velocity.x, this.velocity.z)
      let diff = targetRotation - this.mesh.rotation.y
      while (diff < -Math.PI) diff += Math.PI * 2
      while (diff > Math.PI) diff -= Math.PI * 2
      this.mesh.rotation.y += diff * 7 * delta
    } else {
      this.velocity.x = 0
      this.velocity.z = 0
      this.state = 'idle'
    }

    return true
  }

  updateAI(delta: number, canSwim = false) {
    this.passiveBehavior(delta, canSwim)
  }

  // full passive behavior stack: boarded > panic > follow > tempt > wander.
  // Kept separate from updateAI so day-neutral hostiles (spider) can reuse it.
  passiveBehavior(delta: number, canSwim = false) {
    if (this.isBoarded) {
      this.followTarget = null
      this.velocity.x = 0
      this.velocity.z = 0
      this.state = 'idle'
      return
    }

    if (!this.grounded && !canSwim) return
    if (this.updatePanic(delta)) return
    if (this.updateFollowTarget(delta)) return
    if (this.updateTempt(delta)) return

    this.stateTimer -= delta
    if (this.stateTimer <= 0) {
      if (this.state === 'idle') {
        this.state = 'walking'
        this.stateTimer = 1.5 + Math.random() * 3
        const angle = Math.random() * Math.PI * 2
        this.targetDir.set(Math.cos(angle), 0, Math.sin(angle)).normalize()
      } else {
        this.state = 'idle'
        this.stateTimer = 1 + Math.random() * 4
        this.targetDir.set(0,0,0)
      }
    }

    if (this.state === 'walking') {
      this.velocity.x = this.targetDir.x * this.speed
      this.velocity.z = this.targetDir.z * this.speed
      this.faceVelocity(delta, 5)
    } else {
      this.velocity.x = 0
      this.velocity.z = 0
    }
  }

  // sprint in a roughly-away-from-attacker direction, re-picked every 0.8s
  updatePanic(delta: number) {
    if (this.panicTimer <= 0) return false
    this.panicTimer -= delta
    this.panicDirTimer -= delta

    if (this.panicDirTimer <= 0) {
      this.panicDirTimer = 0.8
      const baseAngle = this.hasDamageDir
        ? Math.atan2(this.lastDamageDir.x, this.lastDamageDir.z)
        : Math.random() * Math.PI * 2
      const angle = baseAngle + (Math.random() - 0.5) * 1.6
      this.targetDir.set(Math.sin(angle), 0, Math.cos(angle))
    }

    this.state = 'walking'
    this.velocity.x = this.targetDir.x * this.speed * 1.8
    this.velocity.z = this.targetDir.z * this.speed * 1.8
    this.faceVelocity(delta, 8)
    return true
  }

  // walk toward a player holding food and stare at them
  updateTempt(delta: number) {
    if (!this.temptTarget || this.isHostile) return false

    const dx = this.temptTarget.position.x - this.mesh.position.x
    const dz = this.temptTarget.position.z - this.mesh.position.z
    const distance = Math.sqrt(dx * dx + dz * dz)

    if (distance > 2.0) {
      this.velocity.x = (dx / distance) * this.speed * 1.1
      this.velocity.z = (dz / distance) * this.speed * 1.1
      this.state = 'walking'
      this.faceVelocity(delta, 7)
    } else {
      this.velocity.x = 0
      this.velocity.z = 0
      this.state = 'idle'
      // face the player while begging
      const targetRotation = Math.atan2(dx, dz)
      let diff = targetRotation - this.mesh.rotation.y
      while (diff < -Math.PI) diff += Math.PI * 2
      while (diff > Math.PI) diff -= Math.PI * 2
      this.mesh.rotation.y += diff * 6 * delta
    }
    return true
  }

  // rotate the body toward the current velocity direction
  faceVelocity(delta: number, turnSpeed: number) {
    const targetRotation = Math.atan2(this.velocity.x, this.velocity.z)
    let diff = targetRotation - this.mesh.rotation.y
    while (diff < -Math.PI) diff += Math.PI * 2
    while (diff > Math.PI) diff -= Math.PI * 2
    this.mesh.rotation.y += diff * turnSpeed * delta
  }

  despawn() {
    this.isAlive = false
    this.scene.remove(this.mesh)
  }
}
